import React, { useEffect, useRef, useState } from 'react'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../utils/agent.store'
import {
  addToaster,
  API_END_POINT,
  axiosInstance,
  DataType,
  DropdownType,
  fetchPresignedURLSingleUploadAtGenerateDocument,
  fetchTypeStatusForCommunications,
  generateDocumentDataType,
  getBase64forDocumentPreview,
  getSearchedDocumentTemplatesApi,
  hideLoader,
  IEntityFieldType,
  SEND_MANUAL_COMMUNICATION_CONSTANT,
  showLoader,
  updateEntityInstanceCorrespondingToCaseId,
  uploadFileToS3Function,
} from '@app-store'
import { useForm } from 'react-hook-form'
import { SendManualCommunicationProps } from '../send-manual-communication/SendManualCommunication.type'
import { isEmpty, noop, uniqueId } from 'lodash'
import {
  CustomButton,
  HTML_TYPE,
  PaginatedControlledDropDown,
  PdfViewer,
  SvgDownloadGrey,
} from '@ui-kit'
import {
  COMM_TEMPLATE,
  ENTITY_NAME,
  FIELD_NAMES,
  LAYOUT_TYPE_NAMESPACE,
  TASK_STATUS,
  WORKFLOW_STATUS,
} from '../../utils/constants'
import {
  downloadPdfFromBase64,
  fetchLayoutsByEntityName,
  permissionObject,
} from '../../utils/helperFunctions'
import { MULTI_UPLOAD_UTILS, STRINGS } from '../../utils/strings'
import { v4 as uuid } from 'uuid'
import { formatUTCDate } from '../../utils/date.utils'
import { isReadOnlyUser } from '../../utils/permission'
import DocumentPopout from '../send-manual-communication/DocumentPopout'
import { fetchFilePreviewUrlUsingFileName } from '../../apis/exportFile.api'

const GenerateDocumentTask: React.FC<SendManualCommunicationProps> = (
  props: SendManualCommunicationProps
) => {
  const { state: taskState, resource_id: resourceId } = props.task
  const generateDocumentFormData: generateDocumentDataType = {
    template_name: (props.preSelectedTemplateId as unknown as string) ?? '',
  }
  const programPermissions = useAppSelector(
    (state: RootState) => state.clientPermissions?.programPermissions
  )
  const caseData = useAppSelector(
    (state: RootState) => state.workflowData?.caseData
  )
  const userId = useAppSelector((state: RootState) => state?.authUser?.user?.id)
  const dispatch = useAppDispatch()
  const selectedTenantId = useAppSelector(
    (state: RootState) => state.authUser.selectedClientTenant?.id
  )
  const caseKey = useAppSelector(
    (state: RootState) => state?.workflowData?.caseDetails?.caseId
  )

  const caseDocumentRelationshipData = useAppSelector(
    (state: RootState) =>
      state?.workflowData?.relationShipTreeObject?.case?.children?.[
        FIELD_NAMES?.CASE_DOCUMENTS
      ]
  )

  const [templateName, setTemplateName] = useState({ current: '' })
  const [currentPageTemplateName, setCurrentPageTemplateName] =
    useState<number>(0)
  const [sizeTemplateName, setSizeTemplateName] = useState<number>(20)
  const [previewDataWithLink, setPreviewDataWithLink] = useState<string | null>(
    null
  )
  const [allTemplates, setAllTemplates] = useState<DropdownType<string>[]>([])
  const [virtualScrollForTemplate, setVirtualScrollForTemplate] =
    useState<boolean>(true)
  const pdfData = useRef<string>('')
  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    watch,
  } = useForm({
    defaultValues: generateDocumentFormData,
  })

  const addToAttachments = async () => {
    const tempPdfData = pdfData?.current
    if (!tempPdfData) return

    try {
      dispatch(showLoader())

      let blob: Blob
      let file: File
      const isUrl = tempPdfData?.startsWith('http')
      if (!isUrl) {
        const prefixedBase64 = tempPdfData.startsWith(
          'data:application/pdf;base64,'
        )
          ? tempPdfData
          : `data:application/pdf;base64,${tempPdfData}`

        const res = await fetch(prefixedBase64)
        blob = await res.blob()
        file = new File([blob], templateName?.current, {
          type: 'application/pdf',
        })
      } else {
        const res = await fetch(tempPdfData)
        blob = await res.blob()
        const mimeType = blob?.type || 'application/octet-stream'
        const extension =
          mimeType ===
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            ? 'docx'
            : mimeType === 'application/pdf'
            ? 'pdf'
            : 'bin'
        const baseTemplateName =
          templateName.current?.replace(/\.(pdf|docx)$/i, '') || 'attachment'
        file = new File([blob], `${baseTemplateName}.${extension}`, {
          type: mimeType,
        })
      }

      const caseDocumentEntityData = await fetchLayoutsByEntityName(
        dispatch,
        ENTITY_NAME.CASE_DOCUMENTS_LOWER
      )

      const layoutId = caseDocumentEntityData?.content?.find(
        (item: IEntityFieldType) =>
          item?.type === LAYOUT_TYPE_NAMESPACE?.LAYOUT_LISTING
      )?.id

      const entityId = caseDocumentEntityData?.content?.[0]?.entity_id

      const timeStamp = formatUTCDate(
        new Date().toISOString(),
        'YYYYMMDD_HHmmss[Z]'
      )
      const cleanTemplateName = templateName.current?.replace(
        /\.(pdf|docx)$/i,
        ''
      )
      const extensionForName =
        file?.type === 'application/pdf'
          ? 'pdf'
          : file?.type ===
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          ? 'docx'
          : 'bin'
      const documentName = `${cleanTemplateName}_${caseKey}_${timeStamp}.${extensionForName}`
      const payload = [
        {
          name: documentName,
          entity_id: entityId,
          content_type: file?.type,
          size: file?.size / 1000,
          type: MULTI_UPLOAD_UTILS.ENTITY_NAME,
        },
      ]

      const response = await fetchPresignedURLSingleUploadAtGenerateDocument(
        payload
      )

      const presignedUrl = response?.data?.attachment?.[0]?.url
      const attachmentId = response?.data?.id

      if (file) {
        await uploadFileToS3Function({ presignedUrl, selectedFile: file })
      }

      const caseDocumentCategoryTypes = await fetchTypeStatusForCommunications(
        COMM_TEMPLATE?.CASE_DOCUMENT_TYPE
      )

      const OtherCategoryId = caseDocumentCategoryTypes?.data?.find(
        (item: { id: number; value: string }) =>
          item?.value === COMM_TEMPLATE?.OTHER
      )?.id

      await updateEntityInstanceCorrespondingToCaseId(
        caseDocumentRelationshipData?.table_name ??
          FIELD_NAMES?.C_CASE_DOCUMENTS,
        parseInt(props?.caseId),
        parseInt(entityId ?? ''),
        layoutId,
        {
          record_status: TASK_STATUS?.ACTIVE,
          name: documentName?.replace(/\.(pdf|docx)$/i, ''),
          document_id: attachmentId,
          case_document_type: {
            id: OtherCategoryId ?? 0,
          },
        },
        caseDocumentRelationshipData?.relation_identifier ?? ''
      )

      dispatch(
        addToaster({
          message: STRINGS?.DOCUMENT_SUCCESSFULLY_ADDED_TO_ATTACHMENTS,
          type: 'success',
          id: uuid(),
        })
      )
    } catch (err) {
      console.error(err)
      dispatch(
        addToaster({
          message: STRINGS?.FAILED_TO_ADD_DOCUMENT_TO_ATTACHMENTS,
          type: 'error',
          id: uuid(),
        })
      )
    } finally {
      dispatch(hideLoader())
    }
  }

  const fetchAllTemplates = async (
    page: number,
    limit: number,
    value?: string,
    operator?: string
  ) => {
    try {
      dispatch(showLoader())
      const response = await getSearchedDocumentTemplatesApi(page, limit, [
        {
          search_key: SEND_MANUAL_COMMUNICATION_CONSTANT.PRODUCT,
          operator: SEND_MANUAL_COMMUNICATION_CONSTANT.EQUALS,
          search_value: [props?.productId],
        },
        {
          search_key: SEND_MANUAL_COMMUNICATION_CONSTANT.PROGRAM,
          operator: SEND_MANUAL_COMMUNICATION_CONSTANT.EQUALS,
          search_value: [props?.programId],
        },
        {
          search_key: SEND_MANUAL_COMMUNICATION_CONSTANT.SERVICE,
          operator: SEND_MANUAL_COMMUNICATION_CONSTANT.EQUALS,
          search_value: [props?.serviceId],
        },
        {
          search_key: SEND_MANUAL_COMMUNICATION_CONSTANT.ENABLED,
          operator: SEND_MANUAL_COMMUNICATION_CONSTANT.EQUALS,
          search_value: [SEND_MANUAL_COMMUNICATION_CONSTANT.TRUE],
        },
        {
          search_key: SEND_MANUAL_COMMUNICATION_CONSTANT.DOCUMENT_NAME,
          operator: operator ?? SEND_MANUAL_COMMUNICATION_CONSTANT.CONTAINS,
          search_value: [value ?? ''],
        },
      ])
      const dataResponse = response.data.content
      if (!isEmpty(dataResponse)) {
        setVirtualScrollForTemplate(true)
        const array = dataResponse.map((item: DataType) => ({
          value: item?.id,
          label: item?.document_name,
          include_editable_text: item?.include_editable_text,
          program_services: item?.program_services,
        }))
        setAllTemplates((prevOptions) => {
          return [...(page === 0 ? [] : prevOptions ?? []), ...array]
        })
        setCurrentPageTemplateName(page)
        setSizeTemplateName(limit)
        return dataResponse
      } else {
        setVirtualScrollForTemplate(false)
      }
    } catch (error) {
      setVirtualScrollForTemplate(false)
      console.error(error)
    } finally {
      dispatch(hideLoader())
    }
  }

  const hasManagePermission = () => {
    return permissionObject.Manage_Workflow(
      programPermissions,
      caseData,
      props?.task,
      userId ?? ''
    )
  }

  const handleTemplateOptionChange = (e: DropdownType<string>) => {
    const templateId = e?.value
    templateName.current = e?.label?.toString()
    if (previewDataWithLink) {
      setPreviewDataWithLink(null)
    }
    setValue('template_name', templateId)
    pdfData.current = ''
  }

  const onSubmit = async () => {
    const data = watch()
    noop(data)
    try {
      dispatch(showLoader())
      const response = await getBase64forDocumentPreview(
        parseInt(props?.caseId),
        parseInt(data?.template_name)
      )
      const documentAuditId = response.data.documentAuditId
      pdfData.current = response?.data?.pdfData
      if (response?.data?.documentType === HTML_TYPE.DOCX_UPPPERCASE) {
        const urlResponse = await fetchFilePreviewUrlUsingFileName(
          response.data.pdfData ?? ''
        )
        pdfData.current = urlResponse?.data?.url
      }

      await props?.updateTaskResource?.(props?.task, {
        resource_id: documentAuditId,
        template_id: watch('template_name'),
      })
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(hideLoader())
    }
  }

  const fetchPreviewDocument = async (id: string) => {
    let url = ''
    try {
      if (taskState === WORKFLOW_STATUS.OPEN) {
        const data = watch()
        noop(data)
        dispatch(showLoader())
        const response = await getBase64forDocumentPreview(
          parseInt(props?.caseId),
          parseInt(data?.template_name)
        )

        url = response?.data?.pdfData
        if (response?.data?.documentType === HTML_TYPE.DOCX_UPPPERCASE) {
          const urlResponse = await fetchFilePreviewUrlUsingFileName(
            response.data.pdfData ?? ''
          )
          url = urlResponse?.data?.url
        }
        dispatch(hideLoader())
        return url
      }

      const response = await axiosInstance.get(
        `${API_END_POINT.TEMPLATE}/v1/document-audit/${id}`
      )

      pdfData.current = response?.data?.body || ''
      url = response?.data?.body // default returned url

      if (response?.data?.document_type === HTML_TYPE.DOCX_UPPPERCASE) {
        const urlResponse = await fetchFilePreviewUrlUsingFileName(
          response.data.body ?? ''
        )
        pdfData.current = urlResponse?.data?.url || ''
        url = urlResponse?.data?.url // override for docx
      }

      setTemplateName({ current: response?.data?.file_name })
    } catch {
      dispatch(
        addToaster({
          id: uniqueId(),
          type: 'error',
          message: STRINGS.SOMETHING_WENT_WRONG,
        })
      )
    }
    dispatch(hideLoader())

    return url as string // 👈 returning the URL
  }

  useEffect(() => {
    if (props?.productId)
      fetchAllTemplates(currentPageTemplateName, sizeTemplateName, '')
  }, [selectedTenantId])

  useEffect(() => {
    const isCompleted = taskState === WORKFLOW_STATUS.COMPLETED

    if (resourceId && isCompleted) {
      fetchPreviewDocument(resourceId)
    }
  }, [props.task])

  const handleAddToAttachments = (e: Event) => {
    const eventTask = (e as CustomEvent)?.detail
    if (eventTask?.id === props?.task?.id) addToAttachments()
  }

  useEffect(() => {
    window.addEventListener('triggerAddToAttachments', handleAddToAttachments)
    return () => {
      window.removeEventListener(
        'triggerAddToAttachments',
        handleAddToAttachments
      )
    }
  }, [])

  return (
    <div className="flex flex-col font-primary bg-accent-0">
      <form className="py-4" method="POST" onSubmit={handleSubmit(onSubmit)}>
        <div className="pl-3 pt-3 pr-3 bg-accent-0 pb-3">
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-2">
              <PaginatedControlledDropDown
                name={`template_name`}
                label={COMM_TEMPLATE.DOCUMENT_TEMPLATE}
                placeholder={templateName.current}
                errors={errors}
                control={control}
                onChange={handleTemplateOptionChange}
                options={allTemplates ?? []}
                required={true}
                dataQaId={'template_name'}
                page={currentPageTemplateName}
                limit={sizeTemplateName}
                fetchOptions={fetchAllTemplates}
                virtualScroll={virtualScrollForTemplate}
                tabIndex={props?.tabIndexOffset}
                disabled={props?.isDisabled || !hasManagePermission()}
              />
            </div>
            <div className="col-span-1">
              <CustomButton
                dataQaId="generate-send-enable-disable"
                disabled={
                  isReadOnlyUser() ||
                  !watch('template_name') ||
                  !hasManagePermission()
                }
                isFilled={true}
                className={`h-[2.375rem] flex w-full`}
                type="button"
                tabIndex={props?.tabIndexOffset}
                onClick={() => onSubmit()}
                key={watch('template_name')?.toString()}
              >
                <span className={`font-primary`}>
                  {COMM_TEMPLATE.GENERATE_PREVIEW}
                </span>
              </CustomButton>
            </div>
          </div>
        </div>
        {pdfData?.current && (
          <div className="bg-accent-0 shadow-md pl-3 pr-3 pb-3">
            <div
              className={`template-data mt-4 p-3 border-t-[2px] border-t-accent-900 bg-accent-100`}
            >
              <div className="flex flex-col">
                <div className="flex justify-between items-center">
                  <div className="font-bold text-sm">
                    <span>{COMM_TEMPLATE?.VIEW_DOCUMENT} - </span>
                    <span>{templateName?.current}</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <DocumentPopout
                      source={pdfData?.current?.toString()}
                      generateUrl={() => fetchPreviewDocument(resourceId ?? '')}
                    />
                    <button
                      className="mr-5 cursor-pointer"
                      onClick={() => {
                        const [base, ext] =
                          templateName?.current?.split('.') ?? 'attachment'
                        const fileName = `${base}-${caseKey ?? props?.caseId}.${
                          ext ?? 'pdf'
                        }`
                        downloadPdfFromBase64(pdfData?.current, fileName)
                      }}
                      data-qa-id="download-file"
                      type="button"
                    >
                      <SvgDownloadGrey />
                    </button>
                  </div>
                </div>
                <div className="flex w-full bg-accent-50 mt-2 overflow-clip h-[21cm] overflow-y-auto">
                  <div className="flex w-full justify-center shadow bg-accent-100">
                    <PdfViewer
                      pdfUrl={pdfData?.current}
                      isBase64={!pdfData?.current?.startsWith('http')}
                      allowZoom={false}
                      showPageNumber={false}
                      defaultZoomScale={1.3}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  )
}

export default GenerateDocumentTask
